import {
  DailyCheckInBonus,
  DailyCheckInDay,
  DailyCheckInRewardStatus
} from '@apeiron/reward'
import { NextImage, noForwardProps, useMessageContext } from '@apeiron/temp'
import { Box, Button, styled, Typography } from '@mui/material'
import { useDailyCheckInContext } from '@src/contexts/reward/DailyCheckInContext'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const CardButton = styled(Button, noForwardProps)<{
  __unlocked?: DailyCheckInRewardStatus
}>`
  background-color: black;
  border-radius: 10px;
  border: 4px solid rgba(92, 92, 92, 1);
  display: flex;
  gap: 12px;
  height: 90px;
  justify-content: center;
  padding: 0 16px;
  text-transform: none;
  ${props =>
    props.__unlocked === DailyCheckInRewardStatus.Claimable &&
    'border: 4px solid rgba(255, 170, 0, 1);'}
  ${props =>
    props.__unlocked === DailyCheckInRewardStatus.Claimed &&
    '* { filter: brightness(70%); }'}
`

const CardImage = styled(NextImage)`
  aspect-ratio: 1;
  height: 100%;
  margin-left: 4px;
`

const IndexBox = styled(Box)`
  aspect-ratio: 1;
  height: 50%;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: white;
`

const TextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
`

const QuantityContainer = styled(Box)`
  display: flex;
  font-size: 30px;
  line-height: 30px;
  color: rgba(255, 170, 0, 1);
  font-weight: 700;
  width: 100%;
  align-items: center;
  justify-content: center;
`

const DescriptionText = styled(Typography)`
  color: rgba(131, 131, 131, 1);
`

const RedDot = styled(Box)`
  animation: bouncing 1.25s infinite;
  aspect-ratio: 1;
  background-color: red;
  border-radius: 50%;
  height: 20%;
  position: absolute;
  right: -10px;
  top: -11%;
`

const CheckInBonusCard: FC<Props> = (props: Props) => {
  const { bonusInfo, dayInfo, campaignId, className } = props

  const { claimDailyCheckInBonus, fetchAfterOperation } =
    useDailyCheckInContext()

  const { setInfo } = useMessageContext()

  const { t } = useTranslation()

  const cycle = useMemo(() => {
    if (R.isNil(dayInfo)) return -1

    const cycleList = dayInfo.map(day => day.cycle)

    const minCycle = Math.min(...cycleList)

    return minCycle
  }, [dayInfo])

  const disabled =
    bonusInfo.status === DailyCheckInRewardStatus.Locked ||
    bonusInfo.status === DailyCheckInRewardStatus.Claimed

  const handleOnClaimConfirm = useCallback(async () => {
    if (
      R.isNotNil(campaignId) &&
      bonusInfo.status === DailyCheckInRewardStatus.Claimable
    ) {
      setInfo(null)

      await claimDailyCheckInBonus([
        {
          campaignID: campaignId,
          bonusHash: bonusInfo.bonusHash,
          cycle
        }
      ])
      await fetchAfterOperation()
    }
  }, [
    bonusInfo.bonusHash,
    bonusInfo.status,
    campaignId,
    cycle,
    claimDailyCheckInBonus,
    fetchAfterOperation,
    setInfo
  ])

  const handleOnClick = useCallback(() => {
    setInfo({
      message: t('reward.checkin.bonus_reward_claim'),
      confirm: t('common.confirm'),
      cancel: t('common.cancel'),
      onConfirm: handleOnClaimConfirm
    })
  }, [setInfo, t, handleOnClaimConfirm])

  return (
    <CardButton
      __unlocked={bonusInfo.status}
      className={className}
      disabled={disabled}
      onClick={handleOnClick}
    >
      <IndexBox>{bonusInfo.meta.index + 1}</IndexBox>
      <TextContainer>
        <QuantityContainer>
          +{bonusInfo.rewards[0].quantity}
          <CardImage src={bonusInfo.rewards[0].meta?.image} />
        </QuantityContainer>
        <DescriptionText>{bonusInfo.meta.description}</DescriptionText>
      </TextContainer>
      {bonusInfo.status === DailyCheckInRewardStatus.Claimable && <RedDot />}
    </CardButton>
  )
}

type Props = {
  bonusInfo: DailyCheckInBonus
  dayInfo?: DailyCheckInDay[]
  campaignId?: string
  className?: string
}

export default CheckInBonusCard
